import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { announcementService } from '../../services/announcementService';
import type { AnnouncementType, CreateAnnouncementRequest } from '../../types/announcement';

const TYPE_BADGE: Record<AnnouncementType, string> = {
  info: 'bg-blue-900/50 text-blue-300 border border-blue-700',
  warning: 'bg-amber-900/50 text-amber-300 border border-amber-700',
  maintenance: 'bg-red-900/50 text-red-300 border border-red-700',
};

const emptyForm: CreateAnnouncementRequest = {
  title: '',
  body: '',
  type: 'info',
  starts_at: null,
  ends_at: null,
};

export default function AnnouncementsAdmin() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateAnnouncementRequest>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: () => announcementService.adminList(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => announcementService.adminCreate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      setForm(emptyForm);
      setShowForm(false);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => announcementService.adminDeactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      setDeactivateError(null);
    },
    onError: (err: Error) => setDeactivateError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    createMutation.mutate({
      ...form,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">System Announcements</h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage banners shown to all users. Active announcements appear at the top of every page.
          </p>
        </div>
        <button
          onClick={() => { setShowForm((v) => !v); setFormError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          New Announcement
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-white">Create Announcement</h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Title *</label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
                placeholder="e.g. Scheduled maintenance Saturday 02:00–04:00 UTC"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Body *</label>
              <textarea
                required
                rows={3}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500 resize-none"
                placeholder="Detailed message shown alongside the title."
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as AnnouncementType })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
              >
                <option value="info">Info (blue)</option>
                <option value="warning">Warning (amber)</option>
                <option value="maintenance">Maintenance (red)</option>
              </select>
            </div>

            <div />

            <div>
              <label className="block text-sm text-slate-400 mb-1">Starts at — UTC (optional)</label>
              <input
                type="datetime-local"
                value={form.starts_at ?? ''}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value || null })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Ends at — UTC (optional)</label>
              <input
                type="datetime-local"
                value={form.ends_at ?? ''}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value || null })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {formError && <p className="text-red-400 text-sm">{formError}</p>}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white border border-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {deactivateError && (
        <p className="text-red-400 text-sm bg-red-900/20 border border-red-700/40 rounded-lg px-4 py-2">
          Deactivation failed: {deactivateError}
        </p>
      )}

      {/* Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : announcements.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No announcements yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-700">
              <tr className="text-xs text-slate-400 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Title / Body</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Date Range</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {announcements.map((ann) => (
                <tr key={ann.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 max-w-xs">
                    <p className="font-medium text-white truncate">{ann.title}</p>
                    <p className="text-slate-400 text-xs truncate">{ann.body}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[ann.type as AnnouncementType] ?? TYPE_BADGE.info}`}>
                      {ann.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {ann.active ? (
                      <span className="flex items-center gap-1 text-emerald-400 text-xs">
                        <CheckCircle size={12} /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-slate-500 text-xs">
                        <Clock size={12} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {ann.starts_at || ann.ends_at ? (
                      <span>
                        {ann.starts_at ? new Date(ann.starts_at).toLocaleDateString() : '—'}
                        {' → '}
                        {ann.ends_at ? new Date(ann.ends_at).toLocaleDateString() : '∞'}
                      </span>
                    ) : (
                      <span className="text-slate-500">Always</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {new Date(ann.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {ann.active && (
                      <button
                        onClick={() => deactivateMutation.mutate(ann.id)}
                        disabled={deactivateMutation.isPending}
                        className="flex items-center gap-1 px-3 py-1 text-xs text-amber-400 hover:text-amber-300 border border-amber-700/50 hover:border-amber-600 rounded transition-colors disabled:opacity-50"
                      >
                        <AlertTriangle size={12} />
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
